import React, { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Send, Sparkles as SparkIcon } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactNewsletter = () => {
  const [contact, setContact] = useState({ name: "", email: "", message: "" });
  const [news, setNews] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [subscribing, setSubscribing] = useState(false);

  const submitContact = async (e) => {
    e.preventDefault();
    if (!contact.name || !contact.email || !contact.message) {
      toast.error("Please fill in every magical field.");
      return;
    }
    try {
      setSubmitting(true);
      await axios.post(`${API}/contact`, contact);
      toast.success("Your letter has been sent on a pixie's wing! ✿");
      setContact({ name: "", email: "", message: "" });
    } catch (err) {
      console.error(err);
      toast.error("The pixie dropped your letter. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const submitNewsletter = async (e) => {
    e.preventDefault();
    if (!news) return toast.error("Whisper your email first.");
    try {
      setSubscribing(true);
      const res = await axios.post(`${API}/newsletter`, { email: news });
      if (res.data?.already_subscribed) {
        toast("You're already on the moonbell list!", { icon: "✨" });
      } else {
        toast.success("Welcome to the moonbell list!");
      }
      setNews("");
    } catch (err) {
      console.error(err);
      toast.error("Could not subscribe. Try again, friend.");
    } finally {
      setSubscribing(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-28 md:py-40 bg-enchanted"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-10 grid lg:grid-cols-12 gap-10">
        {/* Contact form */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="lg:col-span-7"
        >
          <span className="font-accent text-xl text-amber-300">Write to Sybelle</span>
          <h2 className="font-heading text-4xl sm:text-5xl text-white mt-3">
            Send a <span className="italic text-gradient-gold">Letter</span>
          </h2>
          <div className="section-divider !mx-0 mt-5" />
          <p className="mt-5 text-emerald-50/75 max-w-xl">
            Have a question, a fan-drawing, or a tiny tale of your own? Slip it
            beneath the fairy door — Sybelle will read every word.
          </p>

          <form
            onSubmit={submitContact}
            className="glass-card p-6 md:p-8 mt-8 space-y-4"
            data-testid="contact-form"
          >
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-emerald-50/70 mb-1.5">Your name</label>
                <input
                  type="text"
                  value={contact.name}
                  onChange={(e) => setContact({ ...contact, name: e.target.value })}
                  className="w-full bg-emerald-950/60 border border-amber-500/20 focus:border-amber-400/60 rounded-xl px-4 py-3 text-white placeholder:text-emerald-50/40 focus:outline-none transition-colors"
                  placeholder="Lila the Reader"
                  data-testid="contact-name"
                />
              </div>
              <div>
                <label className="block text-sm text-emerald-50/70 mb-1.5">Email</label>
                <input
                  type="email"
                  value={contact.email}
                  onChange={(e) => setContact({ ...contact, email: e.target.value })}
                  className="w-full bg-emerald-950/60 border border-amber-500/20 focus:border-amber-400/60 rounded-xl px-4 py-3 text-white placeholder:text-emerald-50/40 focus:outline-none transition-colors"
                  placeholder="moonbells@miara.land"
                  data-testid="contact-email"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm text-emerald-50/70 mb-1.5">Message</label>
              <textarea
                rows={5}
                value={contact.message}
                onChange={(e) => setContact({ ...contact, message: e.target.value })}
                className="w-full bg-emerald-950/60 border border-amber-500/20 focus:border-amber-400/60 rounded-xl px-4 py-3 text-white placeholder:text-emerald-50/40 focus:outline-none transition-colors resize-none"
                placeholder="Dear Sybelle…"
                data-testid="contact-message"
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="btn-gold disabled:opacity-60"
              data-testid="contact-submit"
            >
              <Send size={16} />
              {submitting ? "Sending…" : "Send to the Glade"}
            </button>
          </form>
        </motion.div>

        {/* Newsletter */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="lg:col-span-5"
        >
          <div className="glass-card p-7 md:p-9 relative overflow-hidden">
            <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full bg-amber-400/10 blur-3xl" />
            <SparkIcon className="text-amber-300 mb-4" size={28} />
            <span className="font-accent text-xl text-amber-300">Moonbell Letters</span>
            <h3 className="font-heading text-3xl sm:text-4xl text-white mt-2 leading-snug">
              Sealed with stardust.
            </h3>
            <p className="mt-3 text-emerald-50/75">
              Join the moonbell list for early peeks at new chapters, free
              colouring pages, and gentle reminders to look for fairy doors.
            </p>

            <form
              onSubmit={submitNewsletter}
              className="mt-6 flex flex-col sm:flex-row gap-3"
              data-testid="newsletter-form"
            >
              <div className="relative flex-1">
                <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-emerald-50/50" />
                <input
                  type="email"
                  value={news}
                  onChange={(e) => setNews(e.target.value)}
                  placeholder="your@email.com"
                  className="w-full bg-emerald-950/60 border border-amber-500/20 focus:border-amber-400/60 rounded-full pl-10 pr-4 py-3 text-white placeholder:text-emerald-50/40 focus:outline-none transition-colors"
                  data-testid="newsletter-email"
                />
              </div>
              <button
                type="submit"
                disabled={subscribing}
                className="btn-gold justify-center disabled:opacity-60"
                data-testid="newsletter-submit"
              >
                {subscribing ? "…" : "Subscribe"}
              </button>
            </form>

            <p className="mt-4 text-xs text-emerald-50/50">
              No spam, only stardust. Unsubscribe anytime.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ContactNewsletter;
